import React, { useEffect, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useCurrentUser, useBusiness } from '../../context/hooks';
import { ServiceType } from '../../types/user';
import { SNotification } from '../../services/notification';
import { ServiceLevel } from '../../types/business';
import { DEFAULT_MANAGEMENT_ROUTE, getFirstPermittedContentRoute } from '../../helpers/managementContentRoute';

export const GroupBasePath = '/group';

interface AccessControlWrapperProps {
  children: React.ReactNode;
  requiredPermission?: string;
  requiredServiceLevel?: ServiceLevel[];
  /** Blocks every PINGPAD_MANAGEMENT user, including SUBSCRIPTION_ADMIN — for pages content staff should never see (Dashboard, Business Profile). */
  blockAllManagement?: boolean;
}

const AccessControlWrapper: React.FC<AccessControlWrapperProps> = ({ children, requiredPermission, requiredServiceLevel, blockAllManagement }) => {
  const history = useHistory();
  const params = useParams();
  const { groupId, groupType } = params as { groupId?: string; groupType?: string };
  const currentUser = useCurrentUser();
  const group = useBusiness(groupId ?? '');

  const hasAccess = (): boolean => {
    if (!currentUser) return false;
    if (currentUser.serviceType === ServiceType.PINGPAD) {
      return true;
    }

    // Management users
    const isManagement = currentUser.serviceType === ServiceType.PINGPAD_MANAGEMENT;
    if (isManagement) {
      if (blockAllManagement) {
        return false;
      }

      const perm = currentUser.permissions?.find((p) => p.groupId === groupId);
      const list = perm?.contentPermissions;
      if (list?.includes('SUBSCRIPTION_ADMIN')) {
        return true;
      }
      if (requiredPermission) {
        return list ? list.includes(requiredPermission) : false;
      }

      return false;
    }

    // Regular users
    return true;
  };

  // Check service level requirement (e.g. Bulletin Board requires Premium)
  const meetsServiceLevelRequirement = useMemo(() => {
    if (!requiredServiceLevel || requiredServiceLevel.length === 0) return true;
    if (!groupId) return true;
    if (!group) return true;
    return requiredServiceLevel.includes(group.serviceLevel);
  }, [requiredServiceLevel, groupId, group]);

  useEffect(() => {
    if (!hasAccess()) {
      if (!currentUser) {
        // history.replace('/login');
        return;
      }

      SNotification.warn('You do not have permission to access this page.');

      if (groupId && groupType) {
        const perm = currentUser.permissions?.find((p) => p.groupId === groupId);
        const contentRoute = getFirstPermittedContentRoute(perm?.contentPermissions) ?? DEFAULT_MANAGEMENT_ROUTE;
        history.replace(`${GroupBasePath}/packages/${groupType}/${groupId}/${contentRoute}`);
      } else {
        // No business context (e.g. top-level dashboard route) — let Home/useHomeRedirect recompute the landing page.
        history.replace('/');
      }
      return;
    }

    if (!meetsServiceLevelRequirement && requiredServiceLevel) {
      SNotification.warn(`This feature is available on ${requiredServiceLevel.join(', ')} plan only. Please upgrade to access this feature.`);
      if (groupId && groupType) {
        history.replace(`${GroupBasePath}/packages/${groupType}/${groupId}/service-plan`);
      }
    }
  }, [hasAccess, meetsServiceLevelRequirement, currentUser, groupId, groupType, history]);

  if (!hasAccess()) {
    return null;
  }

  if (!meetsServiceLevelRequirement) {
    return null;
  }

  return <>{children}</>;
};

export default AccessControlWrapper;
